<template>
  <el-dialog title="修改密码" :visible.sync="visible" width="400px" @closed="handleClose">
    <el-form :model="form" :rules="rules" ref="passwordForm" label-width="100px" label-position="right">
      <el-form-item label="旧密码" prop="old_password" v-if="!form.use_code">
        <el-input type="password" v-model="form.old_password" show-password placeholder="请输入当前密码"></el-input>
      </el-form-item>
      
      <el-form-item label="验证码" prop="code" v-if="form.use_code">
        <div class="code-input-group">
          <el-input v-model="form.code" placeholder="请输入验证码"></el-input>
          <el-button type="primary" :disabled="codeBtnDisabled" @click="sendVerificationCode">
            {{ codeBtnText }}
          </el-button>
        </div>
      </el-form-item>
      
      <el-form-item label="新密码" prop="new_password">
        <el-input type="password" v-model="form.new_password" show-password placeholder="请输入新密码"></el-input>
      </el-form-item>
      
      <el-form-item label="确认新密码" prop="confirm_password">
        <el-input type="password" v-model="form.confirm_password" show-password placeholder="请再次输入新密码"></el-input>
      </el-form-item>
      
      <el-form-item>
        <el-checkbox v-model="form.use_code">使用验证码修改</el-checkbox>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" @click="changePassword" :loading="loading">确认修改</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PasswordDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userProfile: {
      type: Object,
      default: null
    }
  },
  data() {
    // 验证确认密码
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.form.new_password) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };
    
    return {
      visible: false,
      loading: false,
      codeBtnDisabled: false,
      codeBtnText: '获取验证码',
      codeCountdown: 60,
      form: {
        old_password: '',
        new_password: '',
        confirm_password: '',
        code: '',
        use_code: false
      },
      rules: {
        old_password: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        new_password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度至少为6个字符', trigger: 'blur' }
        ],
        confirm_password: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { len: 6, message: '验证码长度为6位', trigger: 'blur' }
        ]
      }
    };
  },
  watch: {
    value(val) {
      this.visible = val;
      if (val) {
        this.resetForm();
      }
    },
    visible(val) {
      this.$emit('input', val);
    }
  },
  methods: {
    // 重置表单
    resetForm() {
      this.form = {
        old_password: '',
        new_password: '',
        confirm_password: '',
        code: '',
        use_code: false
      };
      
      // 如果表单已被初始化，则重置校验结果
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.resetFields();
      }
    },
    
    // 处理对话框关闭
    handleClose() {
      this.resetForm();
    },
    
    // 发送验证码
    async sendVerificationCode() {
      if (!this.userProfile || !this.userProfile.phone) {
        this.$message.error('未找到您的手机号，无法发送验证码');
        return;
      }
      
      try {
        this.codeBtnDisabled = true;
        
        const response = await axios.post('/api/v1/public/sms/send', {
          phone: this.userProfile.phone,
          code_type: 'reset'
        });
        
        if (response.data && response.data.code === 200) {
          this.$message.success('验证码发送成功，请查收短信');
          
          // 开始倒计时
          this.codeCountdown = 60;
          this.codeBtnText = `重新获取(${this.codeCountdown}s)`;
          
          const countdownInterval = setInterval(() => {
            this.codeCountdown--;
            this.codeBtnText = `重新获取(${this.codeCountdown}s)`;
            
            if (this.codeCountdown <= 0) {
              clearInterval(countdownInterval);
              this.codeBtnDisabled = false;
              this.codeBtnText = '获取验证码';
            }
          }, 1000);
          
          // 测试环境下，如果响应中包含测试验证码，则自动填充
          if (response.data.data && response.data.data.test_code) {
            this.form.code = response.data.data.test_code;
          }
        } else {
          this.$message.error('验证码发送失败：' + (response.data.msg || '未知错误'));
          this.codeBtnDisabled = false;
        }
      } catch (error) {
        console.error('验证码发送失败:', error);
        this.$message.error('验证码发送失败，请稍后重试');
        this.codeBtnDisabled = false;
      }
    },
    
    // 修改密码
    changePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) return false;
        
        // 表单验证通过，发送请求
        this.loading = true;
        
        try {
          const token = localStorage.getItem('token');
          const requestData = {
            new_password: this.form.new_password
          };
          
          // 根据是否使用验证码方式，添加不同的参数
          if (this.form.use_code) {
            requestData.code = this.form.code;
            requestData.use_code = true;
          } else {
            requestData.old_password = this.form.old_password;
          }
          
          const response = await axios.put('/api/v1/profile/password', requestData, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data && response.data.code === 200) {
            this.$message.success('密码修改成功，请使用新密码重新登录');
            this.visible = false;
            
            // 通知父组件密码修改成功
            this.$emit('password-changed');
          } else {
            this.$message.error('密码修改失败：' + (response.data.msg || '未知错误'));
          }
        } catch (error) {
          console.error('密码修改失败:', error);
          let errorMsg = '密码修改失败，请稍后重试';
          
          if (error.response && error.response.data && error.response.data.msg) {
            errorMsg = error.response.data.msg;
          }
          
          this.$message.error(errorMsg);
        } finally {
          this.loading = false;
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.code-input-group {
  display: flex;
  
  .el-input {
    margin-right: 10px;
    flex: 1;
  }
  
  .el-button {
    white-space: nowrap;
  }
}
</style> 